
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import DraftPoPicker from '@/components/purchase-order/DraftPoPicker.vue';
import { IAddToPoProduct } from '@/models/add_to_po';
import { EventBus } from '@/main';
import { post } from '@/services/http';
import { mapAddToPoError } from '@/components/purchase-order/add_to_po_drawer_helpers';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { resolveUnitCost, fetchSupplierCostMap, fetchExchangeRate } from '@/helpers/poCostResolver';
import type { CostSource } from '@/helpers/poCostResolver';
import { isUsableImageUrl } from '@/helpers/variantImage';

@Component({ components: { DraftPoPicker } })
export default class AddToPoDrawer extends Vue {
  /**
   * The usable-image rule, exposed so the template can call it. A Vue class
   * component cannot call a module function from a template unless it is a
   * class property — without this the v-if evaluates `undefined` as a
   * function and Vue warns at runtime while the build stays green.
   */
  private isUsableImageUrl = isUsableImageUrl;

  @Prop({ type: Boolean, required: true }) readonly value!: boolean;
  @Prop({ type: Object, default: null }) readonly product!: IAddToPoProduct | null;

  private internalOpen = false;
  private orderedQuantity = 0;
  private unitPrice = 0;
  private selectedPoId: number | null = null;
  private draftMatches = 0;
  private submitting = false;
  private errorMessage = '';
  private selectedPo: any = null;
  private lastSavedPoId: number | null = null;
  // US-CURR-013: once the buyer edits the price we never re-resolve over their value.
  private priceTouched = false;
  private resolvedCostSource: CostSource | null = null;
  private resolvedPoCurrency: string | null = null;
  private supplierCostMemo: Record<string, Promise<number | null>> = {};
  // `${sessionGen}:${variantId}` of the last seed, so a fresh open (onValue + onProduct
  // both fire in the same flush) seeds once, while a reopen (sessionGen bumped) or a
  // product swap (variantId changed) re-seeds.
  private seededKey: string | null = null;
  // Count, not a boolean: resolveInitialCost() and applyCostLadderForPo() can be in
  // flight together (open, then a PO picked immediately), and the first to finish
  // must not clear the flag while the other is still resolving.
  private resolveInFlight = 0;
  // Drawer-session generation, bumped by resetForm() on every open. Async resolutions
  // capture it at start and ignore their own completion once it changes, so a request
  // left pending from a previous session (e.g. close + reopen the same product object,
  // which does not refire the product watcher) can neither overwrite the new cost nor
  // keep the reopened drawer's submit blocked.
  private sessionGen = 0;

  @Watch('value', { immediate: true })
  onValue(v: boolean) {
    this.internalOpen = v;
    if (v) {
      this.resetForm();
      // Reopening with the SAME product object does NOT refire onProduct() (Vue watchers
      // ignore unchanged values), so seed here too — otherwise the drawer reopens blank
      // (qty/price 0) and skips initial cost resolution.
      this.seedFromProduct(this.product);
    }
    if (v && this.product != null) {
      try {
        const mixpanel = (window as any).mixpanel;
        if (mixpanel) {
          mixpanel.track('buying_table_add_to_po_drawer_opened', {
            has_supplier: !!this.product.supplierId,
            source: this.product.source,
          });
        }
      } catch (_) { /* ignore telemetry errors */ }
    }
  }

  @Watch('internalOpen')
  onInternalOpen(v: boolean) { this.$emit('input', v); }

  @Watch('product')
  onProduct(p: IAddToPoProduct | null) {
    // The open transition seeds via onValue(); this handles a product swap while the
    // drawer is already open. Seeding while closed is pointless — the next open reseeds.
    if (!this.internalOpen) return;
    this.seedFromProduct(p);
  }

  /**
   * Seed quantity + price from the product and kick off open-time cost resolution.
   * Idempotent per (session, variant): the fresh-open case fires onValue() and
   * onProduct() in the same flush, and only the first should seed.
   */
  private seedFromProduct(p: IAddToPoProduct | null) {
    if (!p) return;
    const key = `${this.sessionGen}:${p.variantId}`;
    if (this.seededKey === key) return;
    this.seededKey = key;
    this.orderedQuantity = p.suggestedQuantity || 1;
    // Seed with the caller's suggestion. When it is a STORE-currency cost this is only
    // a placeholder — resolveInitialCost() replaces it with the supplier's own cost
    // right away, and applyCostLadderForPo() refines it once a draft PO is picked.
    this.unitPrice = p.suggestedUnitPrice || 0;
    this.priceTouched = false;
    this.resolvedCostSource = null;
    this.resolvedPoCurrency = null;
    // Fire-and-forget: the seeded value is already displayed, this only improves it.
    this.resolveInitialCost(p);
  }

  /**
   * US-CURR-013 — resolve step 1 of the ladder as soon as the drawer opens.
   *
   * SupplierProduct.cost is keyed by (supplierId, variantId) and BOTH are known from
   * the row, so the supplier's own cost does not depend on which draft PO is chosen.
   * Waiting for PO selection meant the drawer opened showing the raw store-currency
   * Shopify cost. Conversion (step 2) still waits, since it needs the PO's currency.
   */
  private async resolveInitialCost(p: IAddToPoProduct) {
    if (p.costSourceCurrency !== 'store') return;
    if (p.supplierId == null) return;

    const gen = this.sessionGen;
    this.resolveInFlight++;
    try {
      const supplierCost = await this.fetchSupplierCost(p.supplierId, p.variantId);
      // Bail if the drawer was closed/reopened (stale session), the buyer typed a
      // value, or the drawer moved to another product, while the request was in flight.
      if (gen !== this.sessionGen) return;
      // Once a PO is picked, applyCostLadderForPo() owns the price (it alone knows the
      // PO's currency); the open-time resolver must not clobber that with the raw
      // supplier cost. The `source !== 'supplier'` return below already blocks the only
      // divergent case today, but this keeps the ordering invariant explicit.
      if (this.priceTouched || this.product !== p || this.selectedPo !== null) return;

      // Run the real ladder with isForeignCurrency=false: no PO is selected yet, so
      // there is no target currency to convert into. That makes step 2 unreachable by
      // construction, leaving only "supplier cost wins" — any other source means we
      // keep the seeded value and wait for a PO to be picked.
      const { cost, source } = resolveUnitCost(
        supplierCost, this.unitPrice, null, false, CurrencyHelper.getStoreCurrency(),
      );
      if (source !== 'supplier') return;

      this.unitPrice = cost;
      this.resolvedCostSource = source;
    } finally {
      // Only the current session owns the counter; a stale request must not touch it.
      if (gen === this.sessionGen) this.resolveInFlight--;
    }
  }

  // Buyer edited the price: from here it is their own number, not a resolved/converted
  // estimate. Drop the provenance so the "(estimated)" hint stops describing a value the
  // buyer typed by hand. resolvedPoCurrency is kept — the PO's currency label still holds.
  private onUnitPriceInput() {
    this.priceTouched = true;
    this.resolvedCostSource = null;
  }

  // Normalize unitPrice to a finite, non-negative number — or null if the
  // input is empty, non-numeric, or negative. The `min="0"` HTML attribute
  // on the field is cosmetic only and doesn't prevent programmatic submit,
  // so the disabled-state computation and the submit path gate on this value.
  private get normalizedUnitPrice(): number | null {
    const raw = this.unitPrice;
    if (raw == null || raw === ('' as any)) return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return null;
    return n;
  }

  private get primaryDisabled() {
    if (this.submitting) return true;
    if (!this.orderedQuantity || this.orderedQuantity <= 0) return true;
    if (this.normalizedUnitPrice == null) return true;
    if (this.selectedPoId == null) return true;
    // US-CURR-013: until resolution settles, unitPrice still holds the seeded
    // STORE-currency Shopify cost. Submitting now would POST that raw number into a
    // supplier-currency PO — the exact defect this story fixes.
    if (this.resolvingCost) return true;
    return false;
  }

  private get primaryLabel() {
    return this.selectedPoId ? `Add to PO-${this.selectedPoId}` : 'Add to PO';
  }

  private resetForm() {
    this.errorMessage = '';
    this.selectedPoId = null;
    this.selectedPo = null;
    this.submitting = false;
    this.lastSavedPoId = null;
    this.orderedQuantity = 0;
    this.unitPrice = 0;
    this.draftMatches = 0;
    this.priceTouched = false;
    this.resolvedCostSource = null;
    this.resolvedPoCurrency = null;
    this.supplierCostMemo = {};
    this.seededKey = null;
    // New drawer session: invalidate any resolution still pending from the previous one
    // and clear its in-flight accounting so a stale/hung request cannot keep submit blocked.
    this.resolveInFlight = 0;
    this.sessionGen++;
  }

  private goToPo() {
    if (this.lastSavedPoId == null) return;
    this.internalOpen = false;
    this.$router.push(`/purchase-orders/${this.lastSavedPoId}`);
  }

  private onPoSelected(po: any) {
    this.selectedPo = po;
    // Fire-and-forget: prefill refinement must never block PO selection.
    this.applyCostLadderForPo(po);
  }

  /**
   * US-CURR-013 — re-derive the prefilled unit cost against the SELECTED PO.
   *
   * The buying table hands us a STORE-currency Shopify cost, but the target PO is
   * denominated in its supplier's currency — and we only learn which supplier/currency
   * that is once a draft PO is picked here. So resolve at selection time:
   *   supplier cost (SupplierProduct.cost) -> converted (cost / rate) -> raw.
   * Sources that already supply a supplier-currency cost are left untouched.
   */
  private async applyCostLadderForPo(po: any) {
    this.resolvedCostSource = null;
    this.resolvedPoCurrency = null;
    if (!this.product || !po) return;
    // Only store-currency costs need re-resolving; never convert an already-supplier price.
    if (this.product.costSourceCurrency !== 'store') return;
    // Respect an explicit buyer edit.
    if (this.priceTouched) return;

    const storeCurrency = CurrencyHelper.getStoreCurrency();
    const poCurrency = po.currencyCode || storeCurrency;
    const isForeign = poCurrency !== storeCurrency;
    const supplierId = po.supplierId ?? this.product.supplierId;

    const gen = this.sessionGen;
    this.resolveInFlight++;
    try {
      const [supplierCost, exchangeRate] = await Promise.all([
        this.fetchSupplierCost(supplierId, this.product.variantId),
        isForeign ? fetchExchangeRate(poCurrency, storeCurrency) : Promise.resolve(null),
      ]);

      // The drawer may have been closed/reopened (stale session), or the buyer may have
      // typed a value or picked a different PO, while these requests were in flight.
      if (gen !== this.sessionGen) return;
      if (this.priceTouched || this.selectedPo !== po) return;

      const { cost, source } = resolveUnitCost(
        supplierCost,
        Number(this.product.suggestedUnitPrice) || 0,
        exchangeRate,
        isForeign,
        poCurrency,
      );
      this.unitPrice = cost;
      this.resolvedCostSource = source;
      this.resolvedPoCurrency = poCurrency;
    } finally {
      // Only the current session owns the counter; a stale request must not touch it.
      if (gen === this.sessionGen) this.resolveInFlight--;
    }
  }

  /** True while a supplier-cost / FX lookup is in flight. Gates submit + shows loading. */
  private get resolvingCost(): boolean {
    return this.resolveInFlight > 0;
  }

  /**
   * SupplierProduct.cost for one variant, in the supplier's currency.
   * Memoised per supplier+variant because it is resolved twice per drawer session —
   * once on open, once when a draft PO is picked (usually for the same supplier).
   * The memo is cleared on each open so an edited cost is picked up next time.
   */
  private async fetchSupplierCost(supplierId: number | null, variantId: number): Promise<number | null> {
    if (!supplierId) return null;
    const key = `${supplierId}:${variantId}`;
    if (!(key in this.supplierCostMemo)) {
      this.supplierCostMemo[key] = fetchSupplierCostMap(supplierId)
        .then((costs) => costs.get(Number(variantId)) ?? null);
    }
    return this.supplierCostMemo[key];
  }

  private get unitCostLabel(): string {
    return this.resolvedPoCurrency && this.resolvedPoCurrency !== CurrencyHelper.getStoreCurrency()
      ? `Unit cost (${this.resolvedPoCurrency})`
      : 'Unit cost';
  }

  /** AC5: converted costs are estimates — tell the buyer to confirm. */
  private get costEstimatedHint(): string {
    return this.resolvedCostSource === 'converted'
      ? 'Estimated — converted from store currency. Confirm the supplier price.'
      : '';
  }

  private onDraftMatches(n: number) {
    this.draftMatches = n;
  }

  private handleInput(v: boolean) {
    if (!v && this.submitting) {
      this.internalOpen = true;
      return;
    }
    this.$emit('input', v);
  }

  private close() {
    if (this.submitting) return;
    this.internalOpen = false;
  }

  private async submit() {
    if (!this.product || !this.selectedPoId) return;
    this.submitting = true;
    this.errorMessage = '';
    try {
      await this.submitExisting();
    } catch (e: any) {
      this.handleError(e);
    } finally {
      this.submitting = false;
    }
  }

  private async submitExisting() {
    if (!this.product || !this.selectedPoId) return;
    // Belt-and-braces: primaryDisabled already blocks submit when the unit
    // price is empty/negative/non-numeric, but if something else triggers
    // this path we refuse to send an invalid value.
    const unitPrice = this.normalizedUnitPrice;
    if (unitPrice == null) return;
    // Snapshot inputs BEFORE the await so the POST, telemetry, emit,
    // snackbar, and `lastSavedPoId` all reference the same PO even if the
    // user's selection changes mid-request.
    const poId = this.selectedPoId;
    const product = this.product;
    const qty = this.orderedQuantity;
    const payload = {
      variantId: product.variantId,
      orderedQuantity: qty,
      unitPrice,
      variantImage: product.variantImage || '',
      productName: product.productName,
      variant: product.variant,
      sku: product.sku,
      supplierSku: product.supplierSku,
    };
    await post(`/purchase_orders/${poId}/items`, payload);
    try {
      const mixpanel = (window as any).mixpanel;
      if (mixpanel) {
        mixpanel.track('buying_table_add_to_existing_po_submitted', {
          po_id: poId,
          supplier_id: product.supplierId,
          variant_id: product.variantId,
          had_supplier_mapping: !!product.supplierId,
          qty,
          source: product.source,
        });
      }
    } catch (_) { /* ignore telemetry errors */ }
    this.$emit('added', { poId, variantId: product.variantId });
    EventBus.$emit('show-snackbar', {
      message: `Added to PO-${poId}`,
      color: 'success',
    });
    this.lastSavedPoId = poId;
  }

  private handleError(e: any) {
    const { message, clearSelectedPo } = mapAddToPoError(e);
    this.errorMessage = message;
    if (clearSelectedPo) this.selectedPoId = null;
  }

  private openPurchaseList() {
    this.internalOpen = false;
    EventBus.$emit('open-purchase-list-drawer');
  }
}
